import { useState, useCallback } from 'react';
import { parseApiValidationErrors } from '@/utils/apiValidationErrors';
import { getFriendlyErrorMessage } from '@/utils/getFriendlyErrorMessage';

/**
 * Estado de erros por campo + aplicação de respostas de API com validação (Zod flatten).
 * Use em formulários MUI: `error={!!fieldErrors.campo}` e `helperText={fieldErrors.campo || 'dica'}`.
 */
export function useFormFieldErrors() {
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const clearFieldErrors = useCallback(() => setFieldErrors({}), []);

  const clearField = useCallback((name: string) => {
    setFieldErrors(prev => {
      if (!(name in prev)) return prev;
      const { [name]: _, ...rest } = prev;
      return rest;
    });
  }, []);

  /**
   * Preenche `fieldErrors` a partir do erro da API.
   * Retorna texto para `Alert` apenas quando há `formErrors` ou erro genérico sem mapa de campos.
   */
  const applyApiError = useCallback(
    (error: unknown, genericFallback = 'Erro ao processar solicitação.'): string | null => {
      const parsed = parseApiValidationErrors(error);
      setFieldErrors(parsed.fieldErrors);

      if (parsed.summaryMessage) {
        return parsed.summaryMessage;
      }

      const fromFlatten = [
        ...parsed.formErrorMessages,
        ...Object.entries(parsed.fieldErrors).map(([k, v]) => `${k}: ${v}`)
      ].join(' · ');
      if (fromFlatten.trim()) {
        return fromFlatten;
      }

      return getFriendlyErrorMessage(error, genericFallback);
    },
    []
  );

  return {
    fieldErrors,
    setFieldErrors,
    clearFieldErrors,
    clearField,
    applyApiError
  };
}
